import * as Tone from 'tone';
import { TransportTime } from 'tone/build/esm/core/type/Units';
import { sequences } from '@magenta/music/es6/core';
import { INoteSequence, NoteSequence } from '@magenta/music/es6/protobuf';
import {
  getSequenceDuration,
  getStartOfSequence,
  getEndOfSequence,
  recalculateSequenceMeta,
} from '../magenta-utils';
import { STEPS_PER_QUARTER } from '../../constants';

export enum GenerationMode {
  /** Generate melody based on the last N notes played */
  Rolling = 'rolling',
  /** Generate melody based on first generated sequence */
  Looping = 'looping',
}

export type BaseGeneratorOptions = {
  /** Algorithm to use when selecting seed sequence for each generated sequence */
  mode?: GenerationMode;
  /** Beats per minute */
  tempo: number;
  /** Duration of each generated sequence */
  sequenceDuration?: TransportTime;
  /** Duration of previously generated sequences to use for continued generation */
  lookbackDuration?: TransportTime;
}

export type BaseGeneratorOutputMeta = {
  /** Name of generator that produced the sequence */
  generator: string;
  /** Generator progress (range: [0,1]) */
  progress: number;
  /** Total duration of generated song in seconds */
  duration: number;
};

export type GeneratorOutput = {
  /** Generated sequence */
  sequence: NoteSequence;
  /** Generated sequence metadata */
  meta: BaseGeneratorOutputMeta;
}

export default abstract class BaseGenerator {
  context: any;

  /** Algorithm to use when selecting seed sequence for each generated sequence */
  mode: GenerationMode;

  /** Beats per minute */
  tempo: number;

  /** Duration of each generated sequence */
  sequenceDuration: TransportTime;

  /** Duration of previously generated sequences to use for continued generation */
  lookbackDuration: TransportTime;

  abstract readonly initialized: boolean;

  get stepsPerSecond(): number {
    return sequences.stepsPerQuarterToStepsPerSecond(STEPS_PER_QUARTER, this.tempo);
  }

  get sequenceDurationSeconds() {
    return Tone.Time(this.sequenceDuration).toSeconds();
  }

  get lookbackDurationSeconds() {
    return Tone.Time(this.lookbackDuration).toSeconds();
  }

  // TODO: Determine this based on model and selected checkpoint
  // eslint-disable-next-line class-methods-use-this
  get quantizeInputs(): boolean {
    return true;
  }

  constructor(options: BaseGeneratorOptions, context?: any) {
    this.context = context;
    this.tempo = options.tempo;
    this.sequenceDuration = options.sequenceDuration || '1m';
    this.lookbackDuration = options.lookbackDuration || '4m';
    this.mode = options.mode || GenerationMode.Rolling;
  }

  abstract initialize(): Promise<BaseGenerator>;

  private selectRollingSeed(
    seedSequence: INoteSequence,
    generatedSequence: INoteSequence,
  ): NoteSequence {
    const generatedDuration = getSequenceDuration(generatedSequence);
    if (generatedDuration >= this.lookbackDuration) {
      return getEndOfSequence(generatedSequence, this.lookbackDurationSeconds, this.stepsPerSecond);
    }
    if (generatedDuration) {
      return getEndOfSequence(
        sequences.concatenate([seedSequence, generatedSequence]),
        this.sequenceDurationSeconds,
        this.stepsPerSecond,
      );
    }
    return new NoteSequence(seedSequence);
  }

  private selectLoopingSeed(
    seedSequence: INoteSequence,
    generatedSequence: INoteSequence,
  ): NoteSequence {
    const generatedDuration = getSequenceDuration(generatedSequence);
    if (generatedDuration >= this.lookbackDuration) {
      return getStartOfSequence(generatedSequence, this.lookbackDurationSeconds);
    }
    if (generatedDuration) {
      return getStartOfSequence(
        sequences.concatenate([seedSequence, generatedSequence]),
        this.lookbackDurationSeconds,
        this.stepsPerSecond,
      );
    }
    return new NoteSequence(seedSequence);
  }

  private selectSeed(leadSequence: INoteSequence, melody: INoteSequence): NoteSequence {
    switch (this.mode) {
      case GenerationMode.Rolling:
        return this.selectRollingSeed(leadSequence, melody);
      case GenerationMode.Looping:
        return this.selectLoopingSeed(leadSequence, melody);
      default:
        return new NoteSequence(leadSequence);
    }
  }

  abstract generateSequence(seedSequence: INoteSequence, progress: number): Promise<NoteSequence>;

  async* generate(
    leadSequence: INoteSequence,
    duration: Tone.Unit.Time,
  ): AsyncGenerator<GeneratorOutput> {
    if (!this.initialized) throw new Error('Generator is unintitialzed.');
    let melody = new NoteSequence();
    const durationSeconds = Tone.Time(duration).toSeconds();
    let generatedDuration = 0;
    while (generatedDuration < durationSeconds) {
      const progress = generatedDuration / durationSeconds;
      let nextSeedSequence = this.selectSeed(leadSequence, melody);
      if (this.quantizeInputs) {
        nextSeedSequence = sequences.quantizeNoteSequence(nextSeedSequence, this.stepsPerSecond);
      }
      // eslint-disable-next-line no-await-in-loop
      const generatedSequence = await this.generateSequence(nextSeedSequence, progress);
      let slicedSequence = getStartOfSequence(
        generatedSequence,
        this.sequenceDurationSeconds,
        this.stepsPerSecond,
      );
      if (sequences.isQuantizedSequence(slicedSequence)) {
        slicedSequence = sequences.unquantizeSequence(slicedSequence, this.tempo);
      }
      // Append generated sequence to melody
      const updatedMelody = melody.notes.length
        ? sequences.concatenate([melody, slicedSequence])
        : slicedSequence;
      generatedDuration = getSequenceDuration(updatedMelody, this.stepsPerSecond);
      // Trigger callback with newly generated sequence
      yield {
        // Using slice of updatedMelody instead of slicedSequence.
        // slicedSequence always starts at time 0 instead of last note played
        sequence: recalculateSequenceMeta({
          ...updatedMelody,
          notes: updatedMelody.notes ? updatedMelody.notes.slice(melody.notes.length) : [],
        }),
        meta: {
          generator: '',
          progress: generatedDuration / durationSeconds,
          duration: generatedDuration,
        },
      };
      melody = updatedMelody;
    }
    return melody;
  }
}
